/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {PERMISSIONS} from '../../../components/auth/auth-constants';
import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../../common/directives/list-default-actions.component';
import {stateTitleView} from '../../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../../routing/state-utils';
import {UserApiService, UserModel} from './user-api.service';
import {UserCopyModalComponent} from './user-copy-modal.component';
import {UserEditPageComponent} from './user-edit-page.component';
import {UserListPageComponent} from './user-list-page.component';

export function userStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(UserApiService);
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const localUserProfile = adnRouterHelper.services.localUserProfile();
  const readOnly = () => !permissions.hasNetwork(PERMISSIONS.manageSystem.apiKey)
    && !permissions.hasDataNetworkPermission(PERMISSIONS.dataAdmin.apiKey)
    && !permissions.hasNetwork(PERMISSIONS.axPublisherAdmin.apiKey)
    && !permissions.hasNetwork(PERMISSIONS.axAdvertiserAdmin.apiKey)
    && !permissions.hasAny(PERMISSIONS.axPublisherTeamAdmin.apiKey)
    && !permissions.hasAny(PERMISSIONS.axAdvertiserTeamAdmin.apiKey)
    && !permissions.hasNetwork(PERMISSIONS.selfServiceUserAdmin.apiKey)
    && !permissions.hasNetworkPermission('POLITICAL_ADMIN', null, 'POLITICAL');
  const rh = adnRouterHelper.createRouterHelper('users.user', UserApiService, 'app.adm', 'app/admin');
  const copyConfig = {
    modalComponent: UserCopyModalComponent,
    stateUrl: 'app.adm.users.user',
    lcase: 'user',
    ucase: 'User'
  };

  states.add(rh.listPath(), rh.list({
    data: {app: ['ADVERTISING', 'DATA', 'POLITICAL']},
    resolve: {listReadOnly: readOnly},
    views: {
      'content@app': {component: UserListPageComponent, bindings: {readOnly: 'listReadOnly'}},
      'context@app': {component: ListDefaultActionsComponent, bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}},
      'title@app': stateTitleView()
    }
  }));
  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    data: {app: ['ADVERTISING', 'DATA', 'POLITICAL']},
    resolve: {
      userPage: function($stateParams: any) {
        if ($stateParams.isNew) {
          return new SignalAdminResourcePageState<UserModel>(
            {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: [], networkGroups: []},
            true, readOnly(), api, 'User', 'username', copyConfig
          );
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => {
          let blocked: string | undefined;
          if (model.id === localUserProfile.get('userId')) {
            blocked = 'Cannot archive or delete yourself from the system.';
          } else if (model.username === 'adnuntius@adnuntius.com'
            && localUserProfile.get('username') !== 'adnuntius@adnuntius.com') {
            blocked = 'This user cannot be archived or deleted.';
          }
          return new SignalAdminResourcePageState(model, false, readOnly(), api, 'User', 'username', copyConfig, blocked);
        });
      },
      model: function(userPage: SignalAdminResourcePageState<UserModel>) {
        return userPage.model();
      }
    },
    views: {
      'content@app': {component: UserEditPageComponent, bindings: {state: 'userPage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'userPage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'userPage'}}
    }
  }));
  return states.build();
}
